import { ErrorNotice, Panel, formatDateTime } from "../components/ui";
import { OrderStatusBadge } from "./OrderStatusBadge";
import { useOrderHistory } from "./useOrders";

/**
 * Everything that has moved this order, oldest first.
 *
 * Read-only, and there is nothing here that could make it otherwise: no route
 * writes an audit row from the Dashboard and none edits one. A mistake is
 * corrected by a new forward transition, which leaves the record of the mistake
 * standing beside its correction — which is the point of having a trail at all.
 *
 * The backend names the four things worth reading — when, from, to, and why —
 * plus which administrator acted. The raw audit payload is deliberately not
 * sent and so is not rendered: an operations screen that prints a JSON blob is
 * one nobody reads, and one that leaks whatever a later feature puts in it.
 */
export function OrderHistoryPanel({ orderId }: { orderId: string }) {
  const history = useOrderHistory(orderId);

  return (
    <Panel
      title={
        <>
          History{" "}
          {history.data ? (
            <span className="orders__count">{history.data.count}</span>
          ) : null}
        </>
      }
      bodyClass=""
    >
      {history.isError ? (
        <div className="panel__body">
          <ErrorNotice error={history.error} />
        </div>
      ) : history.isPending ? (
        <div className="empty" role="status">
          Loading history…
        </div>
      ) : history.data && history.data.events.length === 0 ? (
        // Not an error and not an empty table: a new order genuinely has no
        // history, because being placed is not a transition. Saying so is
        // clearer than a table with no rows in it.
        <div className="empty">
          Nothing has moved this order yet. It was placed and is waiting.
        </div>
      ) : (
        <div className="orders__scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Change</th>
                <th scope="col">Reason</th>
                <th scope="col">By</th>
              </tr>
            </thead>
            <tbody>
              {history.data?.events.map((event, index) => (
                <tr key={`${event.at}-${index}`}>
                  <td className="muted">{formatDateTime(event.at)}</td>
                  <td>
                    {event.from_status && event.to_status ? (
                      <span className="orders__change">
                        <OrderStatusBadge status={event.from_status} />
                        <span aria-hidden="true" className="orders__arrow">
                          →
                        </span>
                        {/* The arrow is decoration; this is what a screen
                            reader hears between the two states. */}
                        <span className="sr-only">became</span>
                        <OrderStatusBadge status={event.to_status} />
                      </span>
                    ) : (
                      // An event the backend recorded under some other action.
                      // Its name is shown rather than a blank cell, so it can
                      // be quoted in a support thread.
                      <span className="muted">{event.action}</span>
                    )}
                  </td>
                  <td>{event.reason ?? "—"}</td>
                  <td className="muted">
                    {/* The account that acted. Never an email address — the
                        backend does not send one. */}
                    {event.actor_display_name ??
                      (event.actor_admin_id ? "An administrator" : "—")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
